'use client';

import { redirect, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Container, Section, Spacer, SpacerSize, TitleMarkup, TrilogyColor, View } from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';
import { setLocalVariable } from '@services/storage';

import { getServerSession } from '@actions/get-session';

import { ProductsItem } from '@app-types/api/statics';
import { EligibilityConfigType } from '@app-types/config';
import { EligibilityError, FastPass, IRudiApiRechercherAdresse, WetoEndEvent } from '@app-types/eligibility';
import { CategoryCodeInfo, MobilePlanType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { FaiUpsellModalContainer } from '@components/fai-upsell-modal';
import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { getCart } from '@helpers/cart';
import { getClientCookies } from '@helpers/cookies-client';
import { replaceWindowUrl } from '@helpers/document';
import { PAGE_SLUG, partials } from '@helpers/partials';
import { paths } from '@helpers/path';
import { sendPageViewTracking } from '@helpers/tracking';

import {
    ELIGIBILITY_ERROR_CODE,
    ELIGIBILITY_STEP_NAME,
    ELIGIBILITY_STEP_STATUS,
    TYPE_ELIGIBILITY,
} from '@constants/eligibility';
import { ELIGIBILITY_JOURNEY, ELIGIBILITY_STORAGE_KEY } from '@constants/provider';
import { MILLIS_SECONDS_IN_A_DAY } from '@constants/time';

import {
    JourneyConfigType,
    getStoredGcpData,
    removeStoredGcpData,
    setThreeClicsStorage,
    useEligibility,
} from '@eligibility/helpers';

import { isReferrerValid } from '../_helpers/eligibility-referrer';
import { EligibilityChecker } from './eligibility-checker';
import { EligibilityResult } from './eligibility-result';
import { EligibilityTitle } from './eligibility-title';
import { EligibilityWidget } from './eligibility-widget';

interface EligibilityProps {
    type?: TYPE_ELIGIBILITY;
    journeyConfig: JourneyConfigType;
    eligibilityConfig: EligibilityConfigType;
    categoryCodeInfo: CategoryCodeInfo[];
    staticsData: ProductsItem[];
    mobilePlans?: MobilePlanType[];
    shouldShowNewFaiCategories: boolean;
}

const appConfig = getAppConfig();

export function Eligibility({
    journeyConfig,
    eligibilityConfig,
    categoryCodeInfo,
    staticsData,
    shouldShowNewFaiCategories,
}: Readonly<EligibilityProps>) {
    const router = useRouter();

    const [type, setType] = useState<TYPE_ELIGIBILITY>(TYPE_ELIGIBILITY.SEARCH);
    const [fastPass, setFastPass] = useState<FastPass>();
    const [user, setUser] = useState<UserType | undefined>();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const planId = searchParams.get('plan_id');
    const addToCart = async (gencodes: string[], redirect: boolean = false, callback?: () => void) => {
        const cart = await getCart(user);
        if (planId) {
            const optionIds: string[] = [];
            const optionsIdsFromParams = searchParams.get('options_id');

            if (optionsIdsFromParams) {
                optionsIdsFromParams.split(',')?.forEach((optionId) => {
                    optionIds.push(optionId);
                });
            }

            const neededProducts: string[] = [];
            const neededProductsFromParam = searchParams.get('needed_products');
            if (neededProductsFromParam) {
                neededProductsFromParam.split(',')?.forEach((gencode) => {
                    neededProducts.push(gencode);
                });
            }

            try {
                await cart.addProductsToCart([planId, ...optionIds, ...neededProducts]);
            } catch (error) {
                console.error('Could not add to cart', error);
            }
        }
        if (searchParams.get('fai_options')) {
            gencodes.push(searchParams.get('fai_options')!);
        }

        try {
            await cart.addProductsToCart(gencodes, true);
            callback?.();
            if (redirect) {
                if (location.search.includes('gamme')) {
                    const currentUrl = location.href;
                    const newUrl = currentUrl.replace('gamme', 'focus');
                    replaceWindowUrl(newUrl);
                }
                router.push(paths.cart);
            }
        } catch {
            console.error(`Could not add ${gencodes.join(', ')} to cart.`);
        }
    };

    const eligibility = useEligibility(eligibilityConfig, journeyConfig, addToCart, searchParams, staticsData, user);

    const shouldShowMinus26Alert =
        journeyConfig.name === ELIGIBILITY_JOURNEY.MOINS_26_ANS ||
        eligibility?.shortList?.rangeNgCommercialSeries?.includes('ss_medium_xgbox');

    const handleResetAddress = () => {
        eligibility.onResetAddress();
        setFastPass(undefined);
        const params = new URLSearchParams(searchParams.toString());
        params.delete('from-widget');

        router.replace(`${pathname}${params.size > 0 ? '?' + params.toString() : ''}`);
    };

    const handleWidgetParams = () => {
        eligibility.setIsFirstStep(false);
        eligibility.setCurrentStep(ELIGIBILITY_STEP_NAME.LOADING_SEARCH);

        let eligData: IRudiApiRechercherAdresse;
        if (searchParams.get('from-widget')) {
            eligData = JSON.parse(decodeURI(searchParams.get('from-widget') ?? ''));
        } else {
            eligData = getStoredGcpData();
        }
        eligibility.setIsMapFirstStep(true);

        try {
            eligibility.setAddress(eligData);
        } catch {
            setType(TYPE_ELIGIBILITY.SEARCH);
            eligibility.setCurrentStep(ELIGIBILITY_STEP_NAME.GMAP);
        }

        removeStoredGcpData();
    };

    const handleError = (error?: EligibilityError) => {
        eligibility.setError(error);
        if (error !== undefined) {
            eligibility.setIsFirstStep(false);
        }

        switch (error?.code) {
            case ELIGIBILITY_ERROR_CODE.ND_CLIENT_EXISTANT:
                //     this.router.navigate([`${this.providerConfig.CONFIG.baseUrl}/${STEP.ERROR.ROUTE}`], {
                //         skipLocationChange: true,
                //     });
                break;
            case ELIGIBILITY_ERROR_CODE.TECHNICAL:
                sendPageViewTracking(false, {
                    ...journeyConfig.tracking,
                    variant: {
                        etape_eligibilite: 'Eligibilite_ko_erreur_technique',
                    },
                    variables: {
                        testParAdresse: type === TYPE_ELIGIBILITY.LINEAR || type === TYPE_ELIGIBILITY.SEARCH,
                    },
                });
                break;
        }
    };

    const handleEnd = async (event: WetoEndEvent['event']) => {
        eligibility.setCurrentStepStatus(ELIGIBILITY_STEP_STATUS.INVALID);
        await eligibility.setResults(type, event.result);
    };

    useEffect(() => {
        const expirationDate = Date.now() + MILLIS_SECONDS_IN_A_DAY;
        if (journeyConfig.referrerConfigKey) {
            const referrerConfig = appConfig[journeyConfig.referrerConfigKey as keyof typeof appConfig] as string[];

            if (referrerConfig?.length && !isReferrerValid(referrerConfig)) {
                redirect(paths.eligibilityTest);
            }

            if (journeyConfig.name === ELIGIBILITY_JOURNEY.BANK) {
                setLocalVariable(
                    ELIGIBILITY_STORAGE_KEY.YAMA_CART,
                    JSON.stringify({
                        data: true,
                        expiresTimestamp: expirationDate,
                    }),
                );
            }
        }

        if (journeyConfig.cookieKey && !getClientCookies(journeyConfig.cookieKey)) {
            redirect(paths.eligibilityTest);
        }

        const url = window.location.pathname + window.location.search.replace('gamme=', 'focus=');
        setLocalVariable(
            ELIGIBILITY_STORAGE_KEY.REFERER,
            JSON.stringify({
                data: url,
                expiresTimestamp: expirationDate,
            }),
        );
    }, []);

    useEffect(() => {
        getServerSession()
            .then((session) => {
                setUser(session?.user as UserType | undefined);
            })
            .catch(() => {
                setUser(undefined);
            });
    }, []);

    useEffect(() => {
        const zipCode = searchParams.get('zipCode');
        const inseeCode = searchParams.get('inseeCode');
        const rivoliCode = searchParams.get('rivoliCode');
        const streetNumber = searchParams.get('streetNumber');
        const extension = searchParams.get('extension');
        const fromWidget = searchParams.get('from-widget');

        if (zipCode && inseeCode && rivoliCode) {
            setFastPass({
                zipCode,
                inseeCode,
                rivoliCode,
                streetNumber: streetNumber || '0',
                extension: extension ?? undefined,
            });
        } else if (fromWidget !== null) {
            handleWidgetParams();
        } else {
            eligibility.setCurrentStep(ELIGIBILITY_STEP_NAME.SINGLE_SEARCH_ELIG);
        }

        if (searchParams.get('parcours3clics')) {
            setThreeClicsStorage({ data: true, expiresTimestamp: null });
        }
    }, [searchParams]);

    return (
        <Section
            className='has-pattern-light'
            backgroundColor={TrilogyColor.NEUTRAL_FADE}
            style={{ minHeight: '90vh' }}
        >
            <Container>
                <View style={{ minHeight: eligibility.results ? 'auto' : '60vh' }}>
                    <EligibilityTitle
                        config={journeyConfig}
                        shortList={eligibility.shortList}
                        results={eligibility.results}
                    />
                    {!eligibility.results && (
                        <EligibilityChecker
                            type={type}
                            config={journeyConfig}
                            setType={setType}
                            currentStep={eligibility.currentStep}
                            currentStepStatus={eligibility.currentStepStatus}
                            isFirstStep={eligibility.isFirstStep}
                            handleEligibilityChange={eligibility.handleEligibilityChange}
                        >
                            <EligibilityWidget
                                type={type}
                                config={journeyConfig}
                                fastPass={fastPass}
                                address={eligibility.address}
                                isMapFirstStep={eligibility.isMapFirstStep}
                                isFirstStep={eligibility.isFirstStep}
                                currentStepStatus={eligibility.currentStepStatus}
                                currentStep={eligibility.currentStep}
                                error={eligibility.error}
                                handleStepStatusChange={eligibility.handleStepStatusChange}
                                handleError={handleError}
                                handleStepChange={eligibility.handleStepChange}
                                handleEnd={handleEnd}
                                user={user}
                            />
                        </EligibilityChecker>
                    )}
                </View>
                {!eligibility.results && shouldShowMinus26Alert && (
                    <>
                        <Spacer size={SpacerSize.FIVE} />
                        <SSIBlock tagId='post-elig-bts-26' url={partials.MARKETING.ELIGIBILITY_26_YEAR_OLD} />
                    </>
                )}
                {eligibility.results && (
                    <EligibilityResult
                        type={type}
                        onResetAddress={handleResetAddress}
                        journeyConfig={journeyConfig}
                        results={eligibility.results}
                        addToCart={addToCart}
                        shortList={eligibility.shortList}
                        openOfferUpsellModal={eligibility.openOfferUpsellModal}
                        categoryCodeInfo={categoryCodeInfo}
                        planId={planId || undefined}
                        shouldShowNewFaiCategories={shouldShowNewFaiCategories}
                    />
                )}
            </Container>
            <Spacer size={SpacerSize.TEN} />
            {!eligibility.results && (
                <Container>
                    <Faq
                        id='faq-offres-internet'
                        titleMarkup={TitleMarkup.H3}
                        title='Vos questions fréquentes sur l’éligibilité à la fibre'
                        url={PAGE_SLUG.ELIGIBILITY}
                    />
                </Container>
            )}
            <TriggerLogin />
            <Container>
                {eligibility.results && <Legals id='post-elig-legals' url={journeyConfig.legalsPath} />}
            </Container>
            {eligibility.upsellModal && <FaiUpsellModalContainer {...eligibility.upsellModal} />}
        </Section>
    );
}
